/**
 * `useVesselTrail` — TanStack Query fetcher for the last N AIS positions
 * of a vessel, with live append from `useShipPositionStream`.
 *
 * Returns:
 *   - `points`: oldest -> newest, capped at `limit` (default 50). FIFO
 *     when a new frame arrives and the buffer is full.
 *   - `metadata`: vessel `name`, `eta`, `nextPort`. Surfaced for the
 *     overlay panel; `eta` and `nextPort` are `null` when AISStream
 *     hasn't broadcast a `ShipStaticData` for the vessel yet.
 *   - `freshestVerifiedAt`: ISO 8601 of the most recent verifiedAt across
 *     the buffer. Drives the source banner under the map.
 *   - All connection status forwarded from `useShipPositionStream`.
 *
 * Zero-mock invariant: every point originates from `ship.recentPositions`
 * (Postgres) or from a live SSE frame (Redis bridge). The hook never
 * synthesises positions.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type {
  ShipRecentPositionsOutput,
  ShipTrailPoint,
} from "@hantavirus/shared";

import {
  useShipPositionStream,
  type ShipPositionFrame,
} from "./useShipPositionStream.js";
import type { SSEStatus } from "./useSSE.js";

const DEFAULT_LIMIT = 50;

/** Producer of `ship.position` events on the wire (mirrors `ShipPosition`). */
function frameToTrailPoint(frame: ShipPositionFrame): ShipTrailPoint {
  const recordedAt =
    frame.recordedAt instanceof Date
      ? frame.recordedAt.toISOString()
      : typeof frame.recordedAt === "string"
        ? frame.recordedAt
        : new Date().toISOString();
  return {
    lat: frame.position.lat,
    lng: frame.position.lng,
    speedKnots: frame.speedKnots ?? null,
    headingDegrees: frame.headingDegrees ?? null,
    recordedAt,
    // SSE doesn't carry verifiedAt today (it's an ingest-side stamp);
    // we use the receive time as the closest available proxy, marked
    // identically to recordedAt when no better signal exists.
    verifiedAt: new Date().toISOString(),
    sourceUrl: "wss://stream.aisstream.io/v0/stream",
  };
}

function buildApiBase(apiBaseUrl?: string): string {
  return (
    apiBaseUrl ??
    (typeof import.meta !== "undefined" && typeof import.meta.env !== "undefined"
      ? ((import.meta.env.VITE_API_URL as string | undefined) ?? "")
      : "")
  ).replace(/\/+$/u, "");
}

async function fetchRecentPositions(
  mmsi: string,
  limit: number,
  apiBaseUrl?: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ShipRecentPositionsOutput> {
  const base = buildApiBase(apiBaseUrl);
  const encoded = encodeURIComponent(JSON.stringify({ json: { mmsi, limit } }));
  const url = `${base}/api/trpc/ship.recentPositions?input=${encoded}`;
  const res = await fetchImpl(url, {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) {
    throw new Error(`ship.recentPositions failed: ${res.status}`);
  }
  const json = (await res.json()) as {
    result?: {
      data?:
        | { json?: ShipRecentPositionsOutput }
        | ShipRecentPositionsOutput;
    };
  };
  const inner = json.result?.data;
  const data =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: ShipRecentPositionsOutput }).json
      : (inner as ShipRecentPositionsOutput | undefined);
  if (!data) throw new Error("ship.recentPositions: malformed response");
  return data;
}

export interface UseVesselTrailOptions {
  /** Max trail points retained (default 50). */
  limit?: number;
  /** Disable the SSE subscription (initial fetch still runs). */
  streamEnabled?: boolean;
  /** Override the API base URL — used by tests. */
  apiBaseUrl?: string;
  /** Custom fetch — used by tests. */
  fetchImpl?: typeof fetch;
}

export interface UseVesselTrailResult {
  /** Oldest -> newest. Capped at `limit`. */
  points: ShipTrailPoint[];
  /** Vessel name from the registry — empty string before initial fetch lands. */
  name: string;
  /** AIS destination — null when unknown. */
  nextPort: string | null;
  /** AIS ETA (ISO 8601) — null when unknown. */
  eta: string | null;
  /** Most recent verifiedAt across the buffer; null when buffer is empty. */
  freshestVerifiedAt: string | null;
  /** SSE connection lifecycle. */
  status: SSEStatus;
  reconnecting: boolean;
  connected: boolean;
  retryCount: number;
  retry: () => void;
  /** True while the initial query is in flight. */
  isInitialLoading: boolean;
  /** Error from the initial query — null on success. */
  error: Error | null;
}

export function useVesselTrail(
  mmsi: string | null | undefined,
  options: UseVesselTrailOptions = {},
): UseVesselTrailResult {
  const {
    limit = DEFAULT_LIMIT,
    streamEnabled = true,
    apiBaseUrl,
    fetchImpl,
  } = options;

  const query = useQuery<ShipRecentPositionsOutput, Error>({
    queryKey: ["ship", "recentPositions", mmsi, limit],
    queryFn: () =>
      fetchRecentPositions(mmsi as string, limit, apiBaseUrl, fetchImpl),
    enabled: Boolean(mmsi),
    staleTime: 0,
    retry: 2,
  });

  const [trail, setTrail] = useState<ShipTrailPoint[]>([]);
  const [name, setName] = useState("");
  const [nextPort, setNextPort] = useState<string | null>(null);
  const [eta, setEta] = useState<string | null>(null);

  // Seed the trail from the initial query exactly once per (mmsi,data)
  // pair. Subsequent renders preserve any SSE-appended points.
  useEffect(() => {
    if (!query.data) return;
    setTrail(query.data.points.slice(-limit));
    setName(query.data.name);
    setNextPort(query.data.nextPort);
    setEta(query.data.eta);
  }, [query.data, limit]);

  // Reset the trail when the MMSI changes — a different vessel must
  // never inherit the previous vessel's points.
  useEffect(() => {
    setTrail([]);
    setName("");
    setNextPort(null);
    setEta(null);
  }, [mmsi]);

  const stream = useShipPositionStream(mmsi, {
    enabled: streamEnabled,
    apiBaseUrl,
  });

  // Append live frames. We track the last SSE frame timestamp to avoid
  // double-applying the initial snapshot (the SSE producer also emits
  // an initial-events snapshot of length 1 on first connect).
  useEffect(() => {
    if (!stream.latest) return;
    setTrail((prev) => {
      const next: ShipTrailPoint = frameToTrailPoint(stream.latest!);
      // Suppress exact-duplicate of the most recent point (same lat/lng
      // at the same recordedAt). This protects against the snapshot
      // landing immediately after a recentPositions fetch.
      const last = prev[prev.length - 1];
      if (
        last &&
        last.lat === next.lat &&
        last.lng === next.lng &&
        last.recordedAt === next.recordedAt
      ) {
        return prev;
      }
      const appended = prev.concat(next);
      // FIFO cap.
      return appended.length > limit
        ? appended.slice(appended.length - limit)
        : appended;
    });
  }, [stream.latest, limit]);

  const freshestVerifiedAt = useMemo(() => {
    if (trail.length === 0) return null;
    return trail[trail.length - 1]!.verifiedAt;
  }, [trail]);

  return {
    points: trail,
    name,
    nextPort,
    eta,
    freshestVerifiedAt,
    status: stream.status,
    reconnecting: stream.reconnecting,
    connected: stream.connected,
    retryCount: stream.retryCount,
    retry: stream.retry,
    isInitialLoading: query.isLoading,
    error: query.error,
  };
}
